import type { ComponentType } from 'react'
import {
  LayoutDashboard,
  Bot,
  Activity,
  Calendar,
  Music,
  Mic2,
  MapPinned,
  ListTodo,
  Mail,
  Wallet,
  Handshake,
  Link2,
  Brain,
  Users,
  Settings,
  BarChart3,
  Target,
  Zap,
  Shield,
  TrendingUp,
  FileSpreadsheet,
  Bell,
  Globe,
  Layers,
  type LucideProps,
} from 'lucide-react'

export interface TutorialStep {
  title: string
  description: string
  icon: ComponentType<LucideProps>
  iconColor: string
  tips?: string[]
}

export interface SectionTutorialData {
  sectionTitle: string
  sectionColor: string
  sectionIcon: ComponentType<LucideProps>
  intro: string
  steps: TutorialStep[]
}

export type SectionId =
  | 'dashboard'
  | 'agents'
  | 'activity'
  | 'schedule'
  | 'music'
  | 'shows'
  | 'touring'
  | 'tasks'
  | 'email'
  | 'money'
  | 'deals'
  | 'links'
  | 'intelligence'
  | 'team'
  | 'settings'

export const SECTION_TUTORIALS: Record<SectionId, SectionTutorialData> = {
  dashboard: {
    sectionTitle: 'Dashboard',
    sectionColor: '#818cf8',
    sectionIcon: LayoutDashboard,
    intro: 'Your command center — a real-time snapshot of everything happening across your career.',
    steps: [
      {
        title: 'Overview at a Glance',
        description: 'The dashboard pulls together your shows, music, tasks, and finances into one view. Each module updates automatically as data flows in.',
        icon: LayoutDashboard,
        iconColor: '#818cf8',
        tips: ['Drag modules to reorder them based on your priorities', 'Click any card to jump directly into that section'],
      },
      {
        title: 'Hero Card',
        description: 'The hero card at the top highlights your most urgent item — typically your next show or a pressing task that needs attention.',
        icon: Target,
        iconColor: '#f59e0b',
        tips: ['The hero card auto-selects based on proximity and urgency', 'It links directly to the full detail view'],
      },
      {
        title: 'Intelligence Signals',
        description: 'Look for colored badges and alerts on dashboard cards. These are intelligence signals — automated insights about ticket pacing, revenue targets, and upcoming deadlines.',
        icon: Zap,
        iconColor: '#FF2300',
        tips: ['Red signals need immediate attention', 'Yellow signals are worth monitoring', 'Green signals mean you\'re on track'],
      },
      {
        title: 'Quick Actions',
        description: 'Use the buttons in each module header to quickly add shows, update tickets, or create tasks without leaving the dashboard.',
        icon: Layers,
        iconColor: '#10b981',
      },
    ],
  },
  agents: {
    sectionTitle: 'Agents',
    sectionColor: '#6366f1',
    sectionIcon: Bot,
    intro: 'Configure AI agents that work on your behalf — handling tasks, drafting emails, and managing your workflow.',
    steps: [
      {
        title: 'Global Voice',
        description: 'Set the overall tone and personality your agents use when communicating. This applies to all automated messages, emails, and responses.',
        icon: Bot,
        iconColor: '#6366f1',
        tips: ['Keep it consistent with your brand voice', 'You can override per-agent later'],
      },
      {
        title: 'How You Work',
        description: 'Tell your agents about your preferences — working hours, communication style, and decision-making habits. This helps them act more like you.',
        icon: Settings,
        iconColor: '#64748b',
        tips: ['Be specific about your preferences', 'Update this as your workflow evolves'],
      },
      {
        title: 'Specialist Agents',
        description: 'Each specialist agent handles a specific domain — touring, social media, email, etc. Configure each one with domain-specific instructions.',
        icon: Layers,
        iconColor: '#f59e0b',
        tips: ['Start with one agent and expand as you get comfortable', 'Review agent actions regularly in the Activity feed'],
      },
    ],
  },
  activity: {
    sectionTitle: 'Activity',
    sectionColor: '#6366f1',
    sectionIcon: Activity,
    intro: 'Your audit trail — every sync, edit, and approval across all your accounts in one timeline.',
    steps: [
      {
        title: 'Source of Truth Progress',
        description: 'The progress bar at the top shows how close you are to having 100% of your data synced and verified. Clear discrepancies to increase it.',
        icon: Target,
        iconColor: '#10b981',
        tips: ['Aim for 100% — it means all your data is aligned', 'Each resolved item increases the percentage'],
      },
      {
        title: 'Activity Feed',
        description: 'Every change — whether from a sync, manual edit, or agent action — appears here with timestamps and attribution.',
        icon: Activity,
        iconColor: '#6366f1',
        tips: ['Filter by section (Shows, Music, etc.) to focus', 'Click any entry to see the full change details'],
      },
      {
        title: 'Approvals',
        description: 'When team members request changes to shared data, those requests appear here for your review. Approve or deny to keep control.',
        icon: Shield,
        iconColor: '#f59e0b',
        tips: ['Pending approvals are highlighted at the top', 'You\'ll get notified of new requests'],
      },
    ],
  },
  schedule: {
    sectionTitle: 'Schedule',
    sectionColor: '#f59e0b',
    sectionIcon: Calendar,
    intro: 'Everything happening on your calendar — Dizee events and Google Calendar in one unified view.',
    steps: [
      {
        title: 'Unified Calendar',
        description: 'Your Dizee events (shows, deadlines, tasks) merge with your Google Calendar into one view. Toggle between day, week, and month views.',
        icon: Calendar,
        iconColor: '#f59e0b',
        tips: ['Connect Google Calendar in Settings for the full picture', 'Shows appear automatically based on their dates'],
      },
      {
        title: 'Event Types',
        description: 'Events are color-coded by type — shows, tasks, meetings, and personal events each have a distinct color so you can scan quickly.',
        icon: Layers,
        iconColor: '#818cf8',
        tips: ['Click any event to see full details', 'Drag events to reschedule them'],
      },
      {
        title: 'Upcoming Focus',
        description: 'The schedule view prioritizes what\'s coming next. Use it to prepare for shows, check travel logistics, and plan your week ahead.',
        icon: TrendingUp,
        iconColor: '#10b981',
      },
    ],
  },
  music: {
    sectionTitle: 'Music',
    sectionColor: '#1DB954',
    sectionIcon: Music,
    intro: 'Your canonical catalog — every song, release, and upcoming project in one organized view.',
    steps: [
      {
        title: 'Catalog Views',
        description: 'Switch between All, Releases, and Upcoming to see your full catalog, grouped releases, or songs in progress. Each view helps with different workflows.',
        icon: Music,
        iconColor: '#1DB954',
        tips: ['Use "Upcoming" to track songs not yet released', 'Releases group songs into projects/albums'],
      },
      {
        title: 'Song Details',
        description: 'Click any song to see its full detail card — release dates, streaming links, collaborators, and metadata. Edit directly from the detail view.',
        icon: FileSpreadsheet,
        iconColor: '#3b82f6',
        tips: ['Keep ISRCs and UPCs up to date for accurate tracking', 'Add streaming links as they become available'],
      },
      {
        title: 'Sync with Sheets',
        description: 'If you\'ve set up Google Sheets integration, your catalog syncs bidirectionally. Changes in either place stay in sync automatically.',
        icon: Zap,
        iconColor: '#f59e0b',
        tips: ['Set up Sheets in Settings → Google Sheets', 'Conflicts are flagged in the Activity feed'],
      },
    ],
  },
  shows: {
    sectionTitle: 'Shows',
    sectionColor: '#9333ea',
    sectionIcon: Mic2,
    intro: 'Track every show from announcement to settlement — ticket counts, guest lists, finances, and logistics.',
    steps: [
      {
        title: 'Show List',
        description: 'All your shows in a sortable table. Each row shows the venue, date, ticket count, and current status at a glance.',
        icon: Mic2,
        iconColor: '#9333ea',
        tips: ['Click column headers to sort', 'Use "Add Show" to create new entries', 'Update Tickets opens a quick counter'],
      },
      {
        title: 'Show Details',
        description: 'Click any show to open its full detail card. You\'ll see ticket tracking, financials, guest list, competing events, and recent activity — all in one place.',
        icon: BarChart3,
        iconColor: '#3b82f6',
        tips: ['Toggle sections on/off in "Show Sections"', 'The AI assistant can answer questions about the show'],
      },
      {
        title: 'Ticket Tracking',
        description: 'Enter ticket counts regularly to track pacing. Dizee calculates sell-through percentage, daily velocity, and projects whether you\'ll sell out.',
        icon: TrendingUp,
        iconColor: '#10b981',
        tips: ['Update counts at least weekly for accurate pacing', 'Red alerts appear if you\'re behind pace', 'Historical entries help build trend lines'],
      },
      {
        title: 'Guest Lists & Financials',
        description: 'Manage your guest list directly from the show detail. Track guarantee, bonuses, expenses, and actual revenue in the Financials section.',
        icon: Wallet,
        iconColor: '#f59e0b',
        tips: ['Share a guest list link for external submissions', 'Set bonus thresholds to auto-calculate payouts'],
      },
    ],
  },
  touring: {
    sectionTitle: 'Touring',
    sectionColor: '#f97316',
    sectionIcon: MapPinned,
    intro: 'Plan and manage tours — travel, hotels, crew, and logistics for all your dates.',
    steps: [
      {
        title: 'Tour Overview',
        description: 'See all your upcoming tour dates on a map and timeline. Group dates into tours for organized planning.',
        icon: MapPinned,
        iconColor: '#f97316',
        tips: ['Use the map view to plan efficient routing', 'Group shows into tours for better organization'],
      },
      {
        title: 'Travel & Hotels',
        description: 'Track flights, ground transport, and hotel bookings for each date. Everything stays linked to the show it belongs to.',
        icon: Globe,
        iconColor: '#3b82f6',
        tips: ['Add confirmation numbers for quick reference', 'Set up crew travel alongside artist travel'],
      },
      {
        title: 'Crew Management',
        description: 'Assign crew members to dates, manage day sheets, and track who\'s on which run. Everyone sees what they need.',
        icon: Users,
        iconColor: '#8b5cf6',
        tips: ['Crew members get their own view of assigned dates', 'Day sheets auto-populate from show details'],
      },
    ],
  },
  tasks: {
    sectionTitle: 'Tasks',
    sectionColor: '#FF6B35',
    sectionIcon: ListTodo,
    intro: 'Track everything that needs doing — from daily to-dos to complex projects across your team.',
    steps: [
      {
        title: 'Task Board',
        description: 'View all tasks in a sortable list. Filter by status, priority, assignee, or due date to focus on what matters right now.',
        icon: ListTodo,
        iconColor: '#FF6B35',
        tips: ['Check tasks off to mark them complete', 'Use priority levels to surface urgent items'],
      },
      {
        title: 'Visibility & Approvals',
        description: 'Tasks can be private (drafts) or visible to the team. Moving a private task to visible may require approval from the artist.',
        icon: Shield,
        iconColor: '#6366f1',
        tips: ['Start with private tasks while planning', '"Move to Visible" submits for approval if required'],
      },
      {
        title: 'Collaboration',
        description: 'Assign tasks to team members, add comments, and track progress. Everyone stays aligned without email back-and-forth.',
        icon: Users,
        iconColor: '#10b981',
        tips: ['Mention team members in comments', 'Due dates trigger reminders automatically'],
      },
    ],
  },
  email: {
    sectionTitle: 'Email',
    sectionColor: '#3b82f6',
    sectionIcon: Mail,
    intro: 'Manage email communications — drafts, templates, and outreach — all connected to your Dizee data.',
    steps: [
      {
        title: 'Email Dashboard',
        description: 'See all your email threads and drafts in one place. Emails are linked to shows, deals, and contacts for full context.',
        icon: Mail,
        iconColor: '#3b82f6',
        tips: ['Use templates for repeating outreach', 'Emails link back to the relevant deal or show'],
      },
      {
        title: 'Smart Drafting',
        description: 'AI agents can draft emails using your voice settings and context from your Dizee data — show details, deal terms, and more.',
        icon: Bot,
        iconColor: '#6366f1',
        tips: ['Review AI drafts before sending', 'Your Global Voice setting controls the tone'],
      },
      {
        title: 'Gmail Integration',
        description: 'Connect Gmail to sync sent/received emails. This gives you a full communication history alongside your Dizee workflow.',
        icon: Globe,
        iconColor: '#10b981',
        tips: ['Connect Gmail in Settings → Google Calendar', 'Only business emails are synced — personal stays private'],
      },
    ],
  },
  money: {
    sectionTitle: 'Money',
    sectionColor: '#10b981',
    sectionIcon: Wallet,
    intro: 'High-level financial awareness — track revenue, expenses, and cash flow across all your activities.',
    steps: [
      {
        title: 'Financial Overview',
        description: 'See total revenue, total spend, and net position at a glance. Filter by time period to analyze different windows.',
        icon: Wallet,
        iconColor: '#10b981',
        tips: ['Use time filters to compare periods', 'Revenue and spend are color-coded throughout'],
      },
      {
        title: 'Adding Entries',
        description: 'Log revenue and expenses with descriptions, vendors, and categories. Entries can be team-visible or private (staging) until you\'re ready to share.',
        icon: FileSpreadsheet,
        iconColor: '#3b82f6',
        tips: ['Use "Private" for estimates you\'re still working on', 'Categories help with end-of-year reporting'],
      },
      {
        title: 'Show & Deal Finances',
        description: 'Financial entries from shows (guarantees, merch) and deals (sponsorships, advances) roll up automatically into your Money view.',
        icon: TrendingUp,
        iconColor: '#f59e0b',
        tips: ['Show financials auto-sync from show details', 'Deal payments appear when logged on the deal'],
      },
    ],
  },
  deals: {
    sectionTitle: 'Deals',
    sectionColor: '#ea580c',
    sectionIcon: Handshake,
    intro: 'Manage your brand partnerships, sponsorships, and business relationships from pitch to payment.',
    steps: [
      {
        title: 'Deal Pipeline',
        description: 'Track deals from initial outreach through negotiation to signed and paid. Each deal has a type, status, and full history.',
        icon: Handshake,
        iconColor: '#ea580c',
        tips: ['Choose the right deal type when creating', 'Move deals through stages as they progress'],
      },
      {
        title: 'Deal Details',
        description: 'Each deal tracks contacts, terms, deliverables, timelines, and payment schedules. All communications link back here.',
        icon: FileSpreadsheet,
        iconColor: '#3b82f6',
        tips: ['Keep contact info up to date for quick outreach', 'Log all terms for easy reference during negotiations'],
      },
      {
        title: 'Financial Tracking',
        description: 'Deal payments and expenses automatically appear in your Money section, giving you a complete financial picture.',
        icon: Wallet,
        iconColor: '#10b981',
        tips: ['Set payment milestones on deals', 'Revenue flows into Money automatically'],
      },
    ],
  },
  links: {
    sectionTitle: 'Links',
    sectionColor: '#0ea5e9',
    sectionIcon: Link2,
    intro: 'Create shareable links to collect data, distribute releases, and manage guest list requests.',
    steps: [
      {
        title: 'Link Types',
        description: 'Create collection links (to gather data from others) or release links (to share music with smart routing to streaming platforms).',
        icon: Link2,
        iconColor: '#0ea5e9',
        tips: ['Collection links are great for press kits and submissions', 'Release links auto-detect the listener\'s preferred platform'],
      },
      {
        title: 'Analytics',
        description: 'Every link tracks views and clicks. See which links are performing and where your traffic is coming from.',
        icon: BarChart3,
        iconColor: '#f59e0b',
        tips: ['Check total views and unique clicks', 'High-performing links can be reused and shared more'],
      },
      {
        title: 'Guest List Links',
        description: 'Create shareable guest list links for shows. Guests submit their info through the link and it flows directly into your show\'s guest list.',
        icon: Users,
        iconColor: '#9333ea',
        tips: ['Share guest list links with your team and VIPs', 'Submissions appear in real-time on the show detail'],
      },
    ],
  },
  intelligence: {
    sectionTitle: 'Intelligence',
    sectionColor: '#FF2300',
    sectionIcon: Brain,
    intro: 'Automated insights about your career — ticket pacing, revenue trends, and actionable recommendations.',
    steps: [
      {
        title: 'Signal Feed',
        description: 'Intelligence surfaces signals from your data — things like slow ticket sales, upcoming deadlines, or revenue milestones that need your attention.',
        icon: Brain,
        iconColor: '#FF2300',
        tips: ['Red signals are critical and need immediate action', 'Signals update in real-time as your data changes'],
      },
      {
        title: 'Ticket Pacing',
        description: 'For every show, Intelligence calculates whether you\'re on pace to sell out. It factors in historical data, time remaining, and current velocity.',
        icon: TrendingUp,
        iconColor: '#10b981',
        tips: ['Pacing uses your ticket update history', 'More frequent updates = more accurate projections'],
      },
      {
        title: 'Actionable Recommendations',
        description: 'Beyond alerts, Intelligence suggests specific actions — "Increase promotion for this show" or "Follow up on this deal before the deadline."',
        icon: Zap,
        iconColor: '#f59e0b',
        tips: ['Act on recommendations to improve your signals', 'Dismissed signals won\'t reappear unless conditions change'],
      },
    ],
  },
  team: {
    sectionTitle: 'Team',
    sectionColor: '#8b5cf6',
    sectionIcon: Users,
    intro: 'Manage your team — invite members, set permissions, and control who can see and do what.',
    steps: [
      {
        title: 'Team Members',
        description: 'See everyone on your team with their roles and access levels. Invite new members by email and they\'ll get onboarded automatically.',
        icon: Users,
        iconColor: '#8b5cf6',
        tips: ['Each member gets their own login', 'Roles determine default permissions'],
      },
      {
        title: 'Permissions',
        description: 'Control exactly what each team member can access — view-only for some sections, full edit for others. Granular control keeps your data safe.',
        icon: Shield,
        iconColor: '#f59e0b',
        tips: ['Start with minimal permissions and expand as needed', 'View-only users can see but not change data'],
      },
      {
        title: 'Collaboration',
        description: 'Team members see only what they have access to. Tasks, shows, and deals are shared based on permissions, keeping everyone focused on their role.',
        icon: Layers,
        iconColor: '#10b981',
        tips: ['Team visibility on tasks requires permissions', 'Activity feed shows who made what changes'],
      },
    ],
  },
  settings: {
    sectionTitle: 'Settings',
    sectionColor: '#64748b',
    sectionIcon: Settings,
    intro: 'Configure your account, integrations, and preferences to make Dizee work exactly how you want.',
    steps: [
      {
        title: 'Account',
        description: 'Update your profile name, username, email, and avatar. Your username appears in shareable links and your public profile.',
        icon: Settings,
        iconColor: '#64748b',
        tips: ['Keep your email current for important notifications', 'Username changes update all your public links'],
      },
      {
        title: 'Google Sheets Integration',
        description: 'Connect a Google Sheet to sync shows, music, and financial data bidirectionally. Click "Configure" to walk through the setup wizard.',
        icon: FileSpreadsheet,
        iconColor: '#10b981',
        tips: ['Follow the setup wizard step by step', 'You\'ll need your Workspace ID and Secret Token'],
      },
      {
        title: 'Google Calendar',
        description: 'Link your Google Calendar to see personal events alongside Dizee events in the Schedule view. Also enables Gmail sync for Email.',
        icon: Calendar,
        iconColor: '#3b82f6',
        tips: ['Only calendar events are synced, not email content', 'Disconnect anytime from this page'],
      },
      {
        title: 'Accessibility',
        description: 'Adjust font size, enable high contrast mode, use a dyslexia-friendly font, or switch between dark and light themes.',
        icon: Bell,
        iconColor: '#f59e0b',
        tips: ['All accessibility settings apply immediately', 'Reset to defaults anytime'],
      },
    ],
  },
}
